import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllPayments,
  getMyPayments,
  getAllParties,
  createPayment,
  updatePayment,
  deletePayment,
  searchPayments,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/paymentSlice";
import { getAllParties as fetchPartiesList, getMyParties as fetchMyPartiesList } from "../../redux/partySlice";
import SearchableSelect from "../common/SearchableSelect";
import { Helmet } from "react-helmet-async";

const Payment = () => {
  const dispatch = useDispatch();
  const { payments, parties, loading, error, success, message } = useSelector(
    (state) => state.payment
  );
  const { user } = useSelector((state) => state.user);

  const getTodayDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const [formData, setFormData] = useState({
    date: getTodayDate(),
    party: "",
    amount: "",
    paymentType: "",
  });

  const [errors, setErrors] = useState({
    date: "",
    party: "",
    amount: "",
    paymentType: "",
  });

  const [touched, setTouched] = useState({
    date: false,
    party: false,
    amount: false,
    paymentType: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMyPayments, setShowMyPayments] = useState(false);

  // Validation functions
  const validateParty = (party) => {
    if (!party || !party.trim()) {
      return "Party is required";
    }
    return "";
  };

  const validateAmount = (amount) => {
    if (!amount && amount !== 0) {
      return "Amount is required";
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      return "Amount must be a valid number";
    }
    if (amountNum < 0) {
      return "Amount cannot be negative";
    }
    if (!Number.isFinite(amountNum)) {
      return "Amount must be a finite number";
    }
    return "";
  };

  const validatePaymentType = (paymentType) => {
    if (!paymentType || !paymentType.trim()) {
      return "Payment type is required";
    }
    const validTypes = ["online", "cash", "check"];
    if (!validTypes.includes(paymentType.toLowerCase())) {
      return "Payment type must be one of: online, cash, check";
    }
    return "";
  };

  const validateDate = (date) => (!date ? "Date is required" : "");

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "date":
        error = validateDate(value);
        break;
      case "party":
        error = validateParty(value);
        break;
      case "amount":
        error = validateAmount(value);
        break;
      case "paymentType":
        error = validatePaymentType(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const validateForm = () => {
    const dateValid = validateField("date", formData.date);
    const partyValid = validateField("party", formData.party);
    const amountValid = validateField("amount", formData.amount);
    const paymentTypeValid = validateField("paymentType", formData.paymentType);
    setTouched({ date: true, party: true, amount: true, paymentType: true });
    return dateValid && partyValid && amountValid && paymentTypeValid;
  };

  // Load payments and parties on component mount
  useEffect(() => {
    dispatch(getAllParties());
    if (showMyPayments && user) {
      dispatch(getMyPayments());
    } else {
      dispatch(getAllPayments());
    }
  }, [dispatch, showMyPayments, user]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Handle input change (support both synthetic event and { target: { name, value } })
  const handleInputChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    if (name == null || name === "") return;
    
    if (name === "amount") {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, "");
      // Prevent multiple decimal points
      const parts = numericValue.split(".");
      const finalValue = parts.length > 2 
        ? parts[0] + "." + parts.slice(1).join("")
        : numericValue;
      
      setFormData((prev) => ({ ...prev, [name]: finalValue }));
      if (touched[name]) {
        validateField(name, finalValue);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const paymentData = {
      date: formData.date || getTodayDate(),
      party: formData.party,
      amount: parseFloat(formData.amount),
      paymentType: formData.paymentType.toLowerCase(),
    };

    try {
      if (isEditing) {
        await dispatch(updatePayment({ id: editingId, paymentData })).unwrap();
      } else {
        await dispatch(createPayment(paymentData)).unwrap();
      }
      
      resetForm();
      setShowModal(false);
      if (showMyPayments && user) {
        dispatch(getMyPayments());
      } else {
        dispatch(getAllPayments());
      }
      // Refresh party list so Party page shows updated due/advance without running script
      dispatch(fetchPartiesList());
      if (user) dispatch(fetchMyPartiesList());
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ date: getTodayDate(), party: "", amount: "", paymentType: "" });
    setErrors({ date: "", party: "", amount: "", paymentType: "" });
    setTouched({ date: false, party: false, amount: false, paymentType: false });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (payment) => {
    const payDate = payment.date || payment.createdAt;
    const dateStr = payDate
      ? `${new Date(payDate).getFullYear()}-${String(new Date(payDate).getMonth() + 1).padStart(2, "0")}-${String(new Date(payDate).getDate()).padStart(2, "0")}`
      : getTodayDate();
    setFormData({
      date: dateStr,
      party: payment.party._id || payment.party,
      amount: payment.amount.toString(),
      paymentType: payment.paymentType,
    });
    setIsEditing(true);
    setEditingId(payment._id);
    setShowModal(true);
    setErrors({ date: "", party: "", amount: "", paymentType: "" });
    setTouched({ date: false, party: false, amount: false, paymentType: false });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this payment?")) {
      try {
        await dispatch(deletePayment(id)).unwrap();
        if (showMyPayments && user) {
          dispatch(getMyPayments());
        } else {
          dispatch(getAllPayments());
        }
        // Refresh party list so Party page shows updated due/advance
        dispatch(fetchPartiesList());
        if (user) dispatch(fetchMyPartiesList());
      } catch (error) {
        // Error handled by Redux
      }
    }
  };

  // Handle search
  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        dispatch(searchPayments(query));
      } else {
        if (showMyPayments && user) {
          dispatch(getMyPayments());
        } else {
          dispatch(getAllPayments());
        }
      }
    },
    [dispatch, showMyPayments, user]
  );

  // Toggle between all payments and my payments
  const handleToggleView = () => {
    setShowMyPayments(!showMyPayments);
    setSearchQuery("");
  };

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `payment-toast payment-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("payment-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("payment-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (success && message) {
      showToast(message, "success");
    }
    if (error) {
      showToast(error, "error");
    }
  }, [success, error, message, showToast]);

  const getPaymentTypeBadge = (type) => {
    const typeLower = type.toLowerCase();
    if (typeLower === "online") return "payment-type-online";
    if (typeLower === "cash") return "payment-type-cash";
    if (typeLower === "check") return "payment-type-check";
    return "";
  };

  return (
    <>
      <Helmet>
        <title>Payment - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes paymentFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes paymentSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes paymentShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .payment-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: paymentFadeIn 0.6s ease;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .payment-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        .payment-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .payment-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .payment-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .payment-search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .payment-search-input {
          flex: 1;
          min-width: 200px;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .payment-search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .payment-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .payment-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .payment-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .payment-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .payment-btn-secondary {
          background: #e1e5e9;
          color: #334155;
        }

        .payment-btn-secondary:hover {
          background: #cbd5e1;
        }

        .payment-btn-toggle {
          background: #10b981;
          color: white;
        }

        .payment-btn-toggle:hover {
          background: #059669;
        }

        .payment-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
        }

        .payment-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .payment-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .payment-table th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .payment-table td {
          padding: 16px;
          border-bottom: 1px solid #e1e5e9;
        }

        .payment-table tbody tr:hover {
          background: #f8f9fa;
        }

        .payment-table tbody tr:last-child td {
          border-bottom: none;
        }

        .payment-action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 8px;
        }

        .payment-btn-edit {
          background: #10b981;
          color: white;
        }

        .payment-btn-edit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .payment-btn-delete {
          background: #ef4444;
          color: white;
        }

        .payment-btn-delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }

        .payment-type-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .payment-type-online {
          background: #dbeafe;
          color: #1e40af;
        }

        .payment-type-cash {
          background: #dcfce7;
          color: #166534;
        }

        .payment-type-check {
          background: #fef3c7;
          color: #92400e;
        }

        .payment-user-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .payment-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .payment-modal-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: paymentSlideIn 0.3s ease;
        }

        .payment-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .payment-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .payment-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .payment-modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .payment-form-group {
          margin-bottom: 20px;
        }

        .payment-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .payment-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .payment-input,
        .payment-select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .payment-input:focus,
        .payment-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .payment-input.error,
        .payment-select.error {
          border-color: #dc2626;
          animation: paymentShake 0.5s ease;
        }

        .payment-error {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .payment-modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .payment-loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .payment-empty {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .payment-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s ease;
          max-width: 350px;
          font-weight: 500;
        }

        .payment-toast.payment-toast-success {
          border-left: 4px solid #059669;
        }

        .payment-toast.payment-toast-error {
          border-left: 4px solid #dc2626;
        }

        .payment-toast.payment-toast-show {
          right: 20px;
          animation: paymentSlideIn 0.3s ease;
        }

        /* Mobile Responsive - Phone First */
        @media (max-width: 768px) {
          .payment-container {
            padding: 15px;
          }

          .payment-title {
            font-size: 2rem;
          }

          .payment-card {
            padding: 20px;
            border-radius: 16px;
          }

          .payment-search-bar {
            flex-direction: column;
          }

          .payment-search-input {
            min-width: 100%;
          }

          .payment-table {
            font-size: 0.9rem;
          }

          .payment-table th,
          .payment-table td {
            padding: 12px 8px;
          }

          .payment-action-btn {
            padding: 6px 12px;
            font-size: 0.85rem;
            margin-right: 4px;
            margin-bottom: 4px;
          }

          .payment-modal-content {
            padding: 20px;
            margin: 10px;
          }

          .payment-modal-title {
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .payment-container {
            padding: 10px;
          }

          .payment-title {
            font-size: 1.5rem;
          }

          .payment-card {
            padding: 15px;
            border-radius: 12px;
          }

          .payment-table {
            font-size: 0.85rem;
          }

          .payment-table th,
          .payment-table td {
            padding: 10px 6px;
          }

          .payment-table th:nth-child(4),
          .payment-table td:nth-child(4) {
            display: none;
          }

          .payment-action-btn {
            padding: 6px 10px;
            font-size: 0.8rem;
            display: block;
            width: 100%;
            margin-bottom: 8px;
            margin-right: 0;
          }

          .payment-modal-content {
            padding: 15px;
            margin: 5px;
          }

          .payment-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
            width: 100%;
            justify-content: center;
          }

          .payment-search-bar {
            gap: 8px;
          }
        }

        @media (max-width: 360px) {
          .payment-container {
            padding: 8px;
          }

          .payment-title {
            font-size: 1.3rem;
          }

          .payment-card {
            padding: 12px;
          }

          .payment-table {
            font-size: 0.8rem;
          }

          .payment-table th,
          .payment-table td {
            padding: 8px 4px;
          }

          .payment-action-btn {
            padding: 4px 8px;
            font-size: 0.75rem;
          }

          .payment-modal-content {
            padding: 12px;
          }

          .payment-btn {
            padding: 8px 12px;
            font-size: 0.85rem;
          }
        }
      `}</style>

      <div className="payment-container">
        <div className="payment-wrapper">
          <div className="payment-header">
            <h1 className="payment-title">Payment Management</h1>
          </div>

          <div className="payment-card">
            <div className="payment-search-bar">
              <input
                type="text"
                className="payment-search-input"
                placeholder="Search payments by party name or payment type..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {user && (
                <button
                  className="payment-btn payment-btn-toggle"
                  onClick={handleToggleView}
                >
                  {showMyPayments ? "Show All" : "Show My Payments"}
                </button>
              )}
              <button
                className="payment-btn payment-btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + Add Payment
              </button>
            </div>

            {loading && <div className="payment-loading">Loading...</div>}

            {!loading && payments.length === 0 && (
              <div className="payment-empty">No payments found</div>
            )}

            {!loading && payments.length > 0 && (
              <div className="payment-table-wrapper">
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Party</th>
                      <th>Amount</th>
                      <th>Payment Type</th>
                      <th>Created By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment._id}>
                        <td>
                          {new Date(payment.date || payment.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          {payment.party ? (
                            <strong>{payment.party.name || "N/A"}</strong>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>₹{parseFloat(payment.amount).toFixed(2)}</td>
                        <td>
                          <span className={`payment-type-badge ${getPaymentTypeBadge(payment.paymentType)}`}>
                            {payment.paymentType}
                          </span>
                        </td>
                        <td>
                          {payment.user ? (
                            <span className="payment-user-badge">
                              {payment.user.name || payment.user.phone || "Unknown"}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          <button
                            className="payment-action-btn payment-btn-edit"
                            onClick={() => handleEdit(payment)}
                          >
                            Edit
                          </button>
                          <button
                            className="payment-action-btn payment-btn-delete"
                            onClick={() => handleDelete(payment._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="payment-modal" onClick={() => setShowModal(false)}>
          <div
            className="payment-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="payment-modal-header">
              <h2 className="payment-modal-title">
                {isEditing ? "Edit Payment" : "Add New Payment"}
              </h2>
              <button
                className="payment-modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="payment-form-group">
                <label className="payment-label required" htmlFor="date">
                  Date
                </label>
                <input
                  id="date"
                  type="date"
                  name="date"
                  value={formData.date || getTodayDate()}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("date")}
                  className={`payment-input ${errors.date && touched.date ? "error" : ""}`}
                  disabled={loading}
                />
                {errors.date && touched.date && (
                  <div className="payment-error">⚠️ {errors.date}</div>
                )}
              </div>
              <div className="payment-form-group">
                <label className="payment-label required" htmlFor="party">
                  Party
                </label>
                <SearchableSelect
                  name="party"
                  options={parties}
                  value={formData.party}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("party")}
                  placeholder="Search party by name or number..."
                  getOptionLabel={(party) => `${party.name} - ${party.number}`}
                  getOptionValue={(party) => party._id}
                  searchFields={["name", "number", "_id"]}
                  className={`payment-select ${
                    errors.party && touched.party ? "error" : ""
                  }`}
                  error={!!(errors.party && touched.party)}
                  disabled={loading}
                  required
                />
                {errors.party && touched.party && (
                  <div className="payment-error">⚠️ {errors.party}</div>
                )}
              </div>

              <div className="payment-form-group">
                <label className="payment-label required" htmlFor="amount">
                  Amount
                </label>
                <input
                  id="amount"
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("amount")}
                  placeholder="Enter amount (e.g., 10000.50)"
                  className={`payment-input ${
                    errors.amount && touched.amount ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.amount && touched.amount && (
                  <div className="payment-error">⚠️ {errors.amount}</div>
                )}
              </div>

              <div className="payment-form-group">
                <label className="payment-label required" htmlFor="paymentType">
                  Payment Type
                </label>
                <select
                  id="paymentType"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("paymentType")}
                  className={`payment-select ${
                    errors.paymentType && touched.paymentType ? "error" : ""
                  }`}
                  disabled={loading}
                >
                  <option value="">Select payment type</option>
                  <option value="online">Online</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                </select>
                {errors.paymentType && touched.paymentType && (
                  <div className="payment-error">⚠️ {errors.paymentType}</div>
                )}
              </div>

              <div className="payment-modal-actions">
                <button
                  type="button"
                  className="payment-btn payment-btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="payment-btn payment-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Payment;

